import camelcaseKeys from "camelcase-keys";
import snakecaseKeys from "snakecase-keys";
import React, {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import axios, { AxiosInstance } from "axios";
import { useAppSelector } from "../hooks/useAppSelector";
import LoadingSpinner from "../components/ui/feedback/LoadingSpinner";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { performLogout } from "./UserAPIUtils";

interface AxiosClientProps {
  children: ReactNode;
  API_BASE_URL: string;
}

// Global axios instance
let axiosInstance: AxiosInstance;
let isAxiosReady = false;
let resolveReady: () => void;
const axiosReadyPromise = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

// Axios Context
const AxiosContext = createContext<AxiosInstance | null>(null);

export const useAxios = () => {
  const context = useContext(AxiosContext);
  if (!context) {
    throw new Error("useAxios must be used within an AxiosProvider");
  }
  return context;
};

export const getAxiosInstance = async (): Promise<AxiosInstance> => {
  if (!isAxiosReady) {
    // console.log("Waiting for Axios to be initialized...");
    await axiosReadyPromise; // Wait until Axios is ready
  }
  return axiosInstance;
};

const AxiosClient: React.FC<AxiosClientProps> = ({
  children,
  API_BASE_URL,
}) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const token = useAppSelector((state) => state.auth.token);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isInitialized) return;

    // console.log("Initializing Axios...");

    axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
    axiosInstance.interceptors.request.use(
      (config) => {
        // console.log("Request interceptor triggered");
        if (
          config.data &&
          typeof config.data === "object" &&
          !(config.data instanceof File)
        ) {
          try {
            config.data = snakecaseKeys(config.data, { deep: true });
          } catch (error) {
            console.error(
              "Error converting request data to snake_case:",
              error,
            );
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        try {
          if (response.data && typeof response.data === "object") {
            response.data = camelcaseKeys(response.data, { deep: true });
          }
        } catch (error) {
          console.error("Error converting response data to camelCase:", error);
        }

        return response;
      },
      (error) => {
        if (
          error.response &&
          error.response.status === 401 &&
          error.response.data.message === "Invalid Authorisation Token"
        ) {
          console.error("Unauthorized: Invalid Authorization Token");
          dispatch(performLogout());
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );

    // console.log("Axios instance configured with baseURL:", API_BASE_URL);

    isAxiosReady = true;
    resolveReady();
    setIsInitialized(true);
  }, [API_BASE_URL, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;

    if (isAuthenticated && token) {
      // console.log("Updating Authorization header");
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      // console.log("Removing Authorization header");
      delete axiosInstance.defaults.headers.Authorization;
    }
  }, [isAuthenticated, token, isInitialized]);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <AxiosContext.Provider value={axiosInstance}>
      {children}
    </AxiosContext.Provider>
  );
};

export { axiosInstance, axiosReadyPromise };
export default AxiosClient;
